import {
  EventCellRenderer,
  HourCellRenderer,
  UpdateCellRenderer,
  DeleteCellRenderer,
  MultiLineTooltip,
  buildEventTooltip,
} from './cellRenderers'

const HOURS = Array.from({ length: 24 }, (_, i) => i + 1)
const LOCKED_STATUSES = new Set(['S', 'L', 'ML'])

const HOURLY_STATUS_LABELS = {
  U: 'Unlocked — editable',
  S: 'Submitted — event submitted to NERC GADS',
  L: 'Locked — daily lock applied by system',
  R: 'Released Lock — editable',
  ML: 'Monthly Lock — entire month locked',
  MU: 'Monthly Unlock — monthly lock removed',
}

export const defaultColDef = {
  resizable: true,
  tooltipComponent: MultiLineTooltip,
}

export const buildColumnDefs = (hasWrite, showH2A, showH3) => {
  const cols = [
    {
      headerName: 'Metric',
      field: 'metric',
      pinned: 'left',
      width: 160,
      sortable: false,
      filter: false,
      editable: false,
      tooltipValueGetter: p => p.value,
      cellStyle: p => {
        if (p.data && p.data.rowType === 'event')
          return { fontStyle: 'italic', color: '#595959' }
        if (p.data && p.data.rowType === 'addEvent')
          return { color: '#1890ff', cursor: 'pointer', fontWeight: 'bold' }
        return { fontWeight: 'bold' }
      },
    },
  ]

  HOURS.forEach(h => {
    if (h === 3 && !showH3) return
    cols.push({
      headerName: `${String(h).padStart(2, '0')}`,
      field: `h${h}`,
      width: 80,
      sortable: false,
      filter: false,
      editable: p => {
        if (!p.context || !p.context.hasWrite) return false
        if (!p.data || p.data.metric !== 'DRL') return false
        if (p.colDef.field === 'h2a') return false
        const hNum = parseInt(p.colDef.field.slice(1), 10)
        const statusRow = p.context && p.context.statusRow
        const hourStatus = statusRow && statusRow[`h${hNum}`]
        return !LOCKED_STATUSES.has(hourStatus)
      },
      cellStyle: p => {
        if (p.data && p.data.metric === 'Hourly Status') {
          if (p.value === 'S')
            return {
              textAlign: 'center',
              backgroundColor: '#d9f7be',
              color: '#389e0d',
              fontWeight: 'bold',
            }
          if (p.value === 'L' || p.value === 'ML')
            return {
              textAlign: 'center',
              backgroundColor: '#ffccc7',
              color: '#cf1322',
              fontWeight: 'bold',
            }
          return { textAlign: 'right' }
        }
        if (
          p.data &&
          (p.data.rowType === 'event' ||
            p.data.metric === 'Unallocated' ||
            p.data.metric === 'DRL')
        ) {
          const hNum = parseInt(p.colDef.field.slice(1), 10)
          const statusRow = p.context && p.context.statusRow
          const hourStatus = statusRow && statusRow[`h${hNum}`]
          if (hourStatus === 'S')
            return { textAlign: 'right', backgroundColor: '#d9f7be' }
          if (hourStatus === 'L' || hourStatus === 'R' || hourStatus === 'ML')
            return { textAlign: 'right', backgroundColor: '#ffccc7' }
          if (p.data.rowType === 'event' && p.value != null && p.value !== '')
            return { textAlign: 'right', backgroundColor: '#f6ffed' }
        }
        return { textAlign: 'right' }
      },
      tooltipValueGetter: p => {
        if (!p.data) return null
        if (p.data.metric === 'DRL')
          return p.data[`drlTooltip_${p.colDef.field}`] || null
        if (p.data.metric === 'Unallocated')
          return p.data[`unallocTooltip_${p.colDef.field}`] || null
        if (p.data.metric === 'Station/Aux Service')
          return p.data[`ssTooltip_${p.colDef.field}`] || null
        if (p.data.metric === 'Hourly Status' && p.value)
          return HOURLY_STATUS_LABELS[p.value] || p.value
        if (p.data.rowType === 'event') {
          const field = p.colDef.field
          if (!field || !field.startsWith('h') || field === 'h2a') return null
          const hNum = parseInt(field.slice(1), 10)
          const events = p.data.eventDataMap && p.data.eventDataMap[hNum]
          return buildEventTooltip(events)
        }
        return null
      },
      cellRendererSelector: p => {
        if (p.data && p.data.rowType === 'addEvent')
          return { component: HourCellRenderer }
        if (
          p.data &&
          p.data.rowType === 'event' &&
          p.value != null &&
          p.value !== ''
        )
          return { component: EventCellRenderer }
        return undefined
      },
      valueFormatter: p => {
        if (!p.data || p.value == null || p.value === '') return ''
        if (
          p.data.metric === 'Hourly Status' ||
          p.data.metric === 'Event Count'
        )
          return String(p.value)
        return Number(p.value).toFixed(1)
      },
    })

    if (h === 2 && showH2A) {
      cols.push({
        headerName: '2A',
        field: 'h2a',
        width: 70,
        sortable: false,
        filter: false,
        editable: false,
        cellStyle: { textAlign: 'right' },
        valueFormatter: p =>
          p.value != null && p.value !== '' ? Number(p.value).toFixed(1) : '',
      })
    }
  })

  if (hasWrite) {
    cols.push({
      headerName: 'Update',
      field: 'update',
      width: 100,
      pinned: 'right',
      sortable: false,
      filter: false,
      editable: false,
      cellRenderer: UpdateCellRenderer,
    })
    cols.push({
      headerName: 'Delete',
      field: 'delete',
      width: 100,
      pinned: 'right',
      sortable: false,
      filter: false,
      editable: false,
      cellRenderer: DeleteCellRenderer,
    })
  }

  return cols
}
